import React, { useState } from 'react';
import { InputAdornment, SelectChangeEvent } from '@mui/material';
import { ArcButton, ArcStack, ArcTabPanel, ArcOutlinedInput, ArcOutlinedSelect, ArcMenuItem } from '@src/core/ui';
import { SectionWrapper, DroneHeader, DroneList, PanelWrapper } from './components';
import { ArcBaseContent, ArcBaseSidebar } from '@src/core/components';
import { OverviewPanel, MaintenancePanel, OrderHistoryPanel } from './panel';
import { FLEET_TABS } from './constants';
import { DronesPageProps } from './types';
import { useNavigate } from 'react-router-dom';
import { useFleetLimitedAccess } from '@src/features/fleet/context/fleet-limited-access-context';
import { useGetHubsQuery } from '@src/features/hubs/redux/api';
import { useGetUserDataQuery } from '@src/core/features';
import { ReactComponent as SearchIcon } from '@src/assets/icons/search.svg';
import { ReactComponent as CloseIcon } from '@src/assets/icons/close.svg';

const ALL_HUBS = 'ALL';

const DronesPage: React.FC<DronesPageProps> = () => {
  const navigate = useNavigate();
  const [searchName, setSearchName] = useState('');
  const [hubFilter, setHubFilter] = useState<string>(ALL_HUBS);
  const fleetLimited = useFleetLimitedAccess();
  const { data: profile } = useGetUserDataQuery();

  const canFilterByHub = !profile?.roleStatus?.isOperator;
  const { data: hubs = [] } = useGetHubsQuery(undefined, { skip: !canFilterByHub });

  const onHubFilterChange = (e: SelectChangeEvent<unknown>) => setHubFilter(String(e.target.value));
  const selectedHubId = hubFilter === ALL_HUBS ? null : Number(hubFilter);

  return (
    <ArcStack direction='row'>
      <ArcBaseSidebar>
        <ArcStack spacing={2} sx={{ height: '100%', minHeight: 0 }}>
          {!fleetLimited && (
            <ArcButton.brand size='large' onClick={() => navigate('add-drone')}>
              Add drone
            </ArcButton.brand>
          )}

          {canFilterByHub && (
            <ArcOutlinedSelect
              label='HUB'
              value={hubFilter}
              onChange={onHubFilterChange}
              fullWidth>
              <ArcMenuItem value={ALL_HUBS}>All</ArcMenuItem>
              {hubs.map((hub) => (
                <ArcMenuItem key={hub.id} value={String(hub.id)}>
                  {hub.name}
                </ArcMenuItem>
              ))}
            </ArcOutlinedSelect>
          )}

          <ArcOutlinedInput
            placeholder='Search'
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            size='small'
            fullWidth
            autoComplete='off'
            sx={{
              '& .MuiInputBase-input': { color: 'white' },
              '& .MuiInputBase-input::placeholder': { color: 'text.secondary', opacity: 1 },
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position='start' sx={{ color: 'text.secondary' }}>
                    <SearchIcon style={{ width: 16, height: 16 }} />
                  </InputAdornment>
                ),
                endAdornment: searchName ? (
                  <InputAdornment position='end' sx={{ color: 'text.secondary' }}>
                    <CloseIcon
                      style={{ width: 16, height: 16, cursor: 'pointer' }}
                      onClick={() => setSearchName('')}
                      role='button'
                      aria-label='Clear search'
                    />
                  </InputAdornment>
                ) : null,
              },
            }}
          />

          <DroneList searchName={searchName.trim()} hubId={selectedHubId} />
        </ArcStack>
      </ArcBaseSidebar>
      <ArcBaseContent>
        <SectionWrapper>
          <DroneHeader />
          <PanelWrapper>
            <ArcTabPanel value={FLEET_TABS[0].value}>
              <OverviewPanel />
            </ArcTabPanel>
            <ArcTabPanel value={FLEET_TABS[1].value}>
              <OrderHistoryPanel />
            </ArcTabPanel>
            <ArcTabPanel value={FLEET_TABS[2].value}>
              <MaintenancePanel />
            </ArcTabPanel>
          </PanelWrapper>
        </SectionWrapper>
      </ArcBaseContent>
    </ArcStack>
  );
};

export default DronesPage;
